import React from 'react';
import DelayedSwipe, { AnimationStatesType, AnimationDirectionsType } from './DelayedSwipe';
import Slider, { Settings } from 'react-slick';

export interface IDelayedSlickSliderProps {
  slidePages: JSX.Element[][];
  delayMs: number;
  animationsDurationMs: number;
  slickSliderClassName: string;
  beforeChange?(currentSlide: number, nextSlide: number, pageSwitchDelayMs: number): void;
  animationDirection?: AnimationDirectionsType;
}

interface SliderRef {
  slickNext(): void;
  slickPrev(): void;
}

function DelayedSlickSlider(
  {
    slidePages,
    delayMs,
    animationsDurationMs,
    slickSliderClassName,
    beforeChange = () => {},
    animationDirection = 'left',
  }: IDelayedSlickSliderProps,
  ref: React.Ref<SliderRef>
): JSX.Element {
  const [style, setStyle] = React.useState<HTMLStyleElement>(null);
  const [currentPage, setCurrentPage] = React.useState<number>(0);

  const slickRef = React.useRef<SliderRef>(null);

  React.useImperativeHandle(ref, () => ({
    slickNext: clickNext,
    slickPrev: clickPrev,
  }));

  const clickNext = () => currentPage < slidePages.length - 1 && slickRef.current && slickRef.current.slickNext();

  const clickPrev = () => currentPage > 0 && slickRef.current && slickRef.current.slickPrev();

  const slideStates = slidePages.map((_, i) =>
    React.useState<AnimationStatesType>(i === 0 ? 'entering' : 'not-visible')
  );

  const calculatePageSwitchDelayMs = function (animationsDurationMs: number): number {
    return animationsDurationMs + 200;
  };

  const settings: Settings = {
    useTransform: false,
    className: slickSliderClassName,
    arrows: true,
    dots: true,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 0,
    adaptiveHeight: false,
    beforeChange: (currentSlide, nextSlide) => {
      if (currentSlide != nextSlide) {
        setCurrentPage(nextSlide);

        const pageSwitchDelayMs: number = calculatePageSwitchDelayMs(animationsDurationMs);
        beforeChange(currentSlide, nextSlide, pageSwitchDelayMs);

        const [, currentSetter] = slideStates[currentSlide];
        const [, nextSetter] = slideStates[nextSlide];

        currentSetter('leaving');

        ((currentSetter, nextSetter) => {
          setTimeout(() => {
            currentSetter('not-visible');
            nextSetter('entering');
          }, pageSwitchDelayMs);
        })(currentSetter, nextSetter);

        const _style = document.createElement('style');
        _style.innerHTML = `
        .${slickSliderClassName} .slick-track {
          transition-delay: ${pageSwitchDelayMs}ms !important;
        }`;

        if (style) {
          document.body.replaceChild(_style, style);
        } else {
          document.body.appendChild(_style);
        }

        setStyle(_style);
      }
    },
  };

  return (
    <>
      <Slider ref={slickRef as React.LegacyRef<Slider> /* FIXME: Bruno GIK-1730 */} {...settings}>
        {slidePages.map((page, i) => (
          <DelayedSwipe
            state={slideStates[i][0]}
            direction={animationDirection}
            delayMs={delayMs}
            animationDurationMs={animationsDurationMs}
          >
            {page}
          </DelayedSwipe>
        ))}
      </Slider>
    </>
  );
}

export default React.forwardRef(DelayedSlickSlider);
